import { downloadAttachment } from 'api/v2/shared/downloadDocument';
import { getFileUrl } from 'api/v2/users';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Loader } from 'components/atoms/Loader';
import useQuery from 'hooks/react-query/useQuery';
import type { FC } from 'react';
import { useId, useMemo } from 'react';
import type { Nullable } from 'types/global.types';
import { getFileExtensionFromPath, getFileNameFromPath } from 'utils/helpers';
import { DownloadOutlineIcon } from 'utils/icons';
import {
  PDFFileIcon,
  PlainFileIcon,
  XMLFileIcon,
} from 'utils/icons/figmaIcons';
import { adaptDownLoadData } from './helpers';

type FileType = {
  label: string;
  downloadName: string;
  path: string;
  type: string;
  className?: string;
};

type ListFileType = {
  label: string;
  downloadName?: string;
  downloadNamePrefix?: string | Nullable<string>[];
  path: Nullable<string>;
  type?: string;
};

type Props = {
  addFileTypeToFileLabels?: boolean;
  addFileTypeToDownloadName?: boolean;
  files: ListFileType[];
};

const fileIconsMap: Record<string, FC<React.SVGProps<SVGSVGElement>>> = {
  pdf: PDFFileIcon,
  xml: XMLFileIcon,
  default: PlainFileIcon,
};

const File: FC<FileType> = (props) => {
  const Icon = fileIconsMap[props.type] || fileIconsMap.default;

  const downloadFile = useQuery(
    [useId(), props.path],
    () => {
      return getFileUrl(props.path!).then(({ url }) =>
        downloadAttachment(url, props.downloadName),
      );
    },
    { enabled: false, gcTime: 0, staleTime: 0 },
  );

  return (
    <Button
      asDefault
      className={classNames(`flex items-center group p-3`, props.className)}
      onClick={() => downloadFile.refetch()}
      loading={downloadFile.isFetching}
    >
      <Icon className="w-6 h-6 mr-2" />
      <span className="text-sm">{props.label}</span>
      {downloadFile.isFetching ? (
        <Loader size="2xl" className="ml-auto p-0.5 flex-shrink-0" />
      ) : (
        <DownloadOutlineIcon className="ml-auto w-6 h-6 flex-shrink-0 text-primary-400 group-hover:text-primary group-focus:text-primary" />
      )}
    </Button>
  );
};

const FilesDownloadList: FC<Props> = (props) => {
  const cleanedFiles = useMemo(
    () =>
      props.files
        .filter((file) => file.path)
        .map((file) =>
          adaptDownLoadData({
            label: file.label,
            path: file.path!,
            downloadName: file.downloadName,
            downloadNamePrefix: file.downloadNamePrefix,
            addFileTypeToFileLabel: props.addFileTypeToFileLabels,
            addFileTypeToDownloadName: props.addFileTypeToDownloadName,
            type: file.type,
          }),
        ),
    [
      props.files,
      props.addFileTypeToDownloadName,
      props.addFileTypeToFileLabels,
    ],
  );

  if (!cleanedFiles.length) return null;
  return (
    <div className="flex flex-col border border-primary-100 rounded-lg ">
      {cleanedFiles.map((file, i) => (
        <File
          key={i}
          {...file}
          className="border-b border-primary-100 last:border-b-0"
        />
      ))}
    </div>
  );
};

export default FilesDownloadList;
